<div class="container">
    <app-section-header
        title="{{ opportunity.name ?? '' }}"
        subTitle="OID {{ opportunity.id }}"
        [actionsTemplate]="actionsTemplate"
        [mainAdditionalTemplate]="statusTemplate"
        [iconName]="'goBack'"
    >
        <ng-template #statusTemplate>
            @if (opportunityStatusId()) {
                <app-opportunity-status-indicator
                    [config]="statusIndicatorConfig()"
                    (changeStatusEvent)="handleStatusChangeFromStatusIndicator($event)"
                    (manualStatusClick)="addManualStatus()"
                    (disqualifyToggleClick)="changeDisqualifiedStatus()"
                />
            }
            @if (!isActive()) {
                <lib-status-indicator
                    [iconName]="'archived'"
                    [colorType]="'error'"
                    [text]="'Archived'"
                ></lib-status-indicator>
            }
        </ng-template>

        <ng-template #actionsTemplate>
            <div class="actions">
                @if (showButtons()) {
                    <lib-helper-info message="Unsaved changes"></lib-helper-info>
                    <div class="editButtons">
                        <button class="secondary-btn" (click)="cancel()">Cancel</button>
                        <button class="primary-btn" [disabled]="isLoading" (click)="saveOpportunity()">Save</button>
                    </div>
                }
                @if (isModelEqual() && hasAcumaticaIntegration()) {
                    <lib-external-link
                        [url]="acumaticaBidUrl()"
                        message="Acumatica Integration"
                        tooltip="There is no linked bid and/or the status is not Accepted"
                        [openInSameTab]="true"
                    ></lib-external-link>
                }
                @if (
                    !baseEstimateExternalUrl() &&
                    isModelEqual() &&
                    canCreateEstimate() &&
                    !opportunity.opportunityManualId &&
                    !canDisqualified()
                ) {
                    <button class="primary-btn" (click)="createBaseEstimate()">Create/Link Bid</button>
                }
                @if (baseEstimateExternalUrl() && isModelEqual()) {
                    <lib-external-link [url]="baseEstimateExternalUrl()" message="DIV25 Sales App"></lib-external-link>
                }
                @if (opportunity.opportunityManualId && isModelEqual()) {
                    <span class="manualStatus">Manual Status</span>
                }
                @if (
                    isModelEqual() &&
                    (canArchive() || canRestore() || (baseEstimateExternalUrl() && canCreateEstimate()))
                ) {
                    <app-kebab-menu>
                        @if (canArchive() && isActive()) {
                            <button role="menuitem" class="resetStyles" (click)="archiveOpportunity()">Archive</button>
                        }
                        @if (canRestore() && !isActive()) {
                            <button role="menuitem" class="resetStyles" (click)="restoreOpportunity()">Restore</button>
                        }
                        @if (baseEstimateExternalUrl()) {
                            <button role="menuitem" class="resetStyles" (click)="unlinkEstimateOpportunity()">
                                Unlink Bid
                            </button>
                        }
                    </app-kebab-menu>
                }
            </div>
        </ng-template>
    </app-section-header>

    <div class="tabGroup">
        <lib-tab-view
            [currentTab]="currentTab()"
            (currentTabChange)="tabChange($event)"
            alignment="start"
            separation="wide"
        >
            <lib-tab-item tabName="Overview" [bodyTemplate]="overviewTab">
                <ng-template #overviewTab>
                    <div class="tab" #scrollable>
                        @if (opportunity.id !== 0) {
                            <app-opportunities-single-overview
                                cdkScrollable
                                #overview
                                [baseEstimateExternal]="!!baseEstimateExternalUrl()"
                                [isReadOnly]="!canEditOpportunity()"
                                (valueChange)="onValueChanged()"
                                [isCreateEstimate]="isCreateEstimate"
                                [actualValue]="actualValue"
                                [termValue]="termValue"
                                [opportunityInput]="opportunity"
                            ></app-opportunities-single-overview>
                        }
                    </div>
                </ng-template>
            </lib-tab-item>
            <lib-tab-item tabName="Bid" [bodyTemplate]="estimatesTab">
                <ng-template #estimatesTab>
                    <app-opportunities-single-estimates
                        [opportunityId]="opportunity.id"
                        [opportunityEstimates]="opportunityEstimates"
                        [canCreateEstimate]="canCreateEstimate()"
                        [manualId]="opportunity.opportunityManualId"
                        [opportunityStatusId]="opportunity.opportunityStatusId"
                        (createEstimate)="createBaseEstimate()"
                    >
                    </app-opportunities-single-estimates>
                </ng-template>
            </lib-tab-item>
            <lib-tab-item tabName="Buildings" [bodyTemplate]="buildingsTab" [headerTemplate]="headerTemplate">
                <ng-template #buildingsTab>
                    <app-opportunities-single-buildings
                        (listEntitiesUpdated)="onTabCountersChange()"
                    ></app-opportunities-single-buildings>
                </ng-template>
            </lib-tab-item>
            <lib-tab-item tabName="Contacts" [bodyTemplate]="contactsTab" [headerTemplate]="headerTemplate">
                <ng-template #contactsTab>
                    <app-opportunities-single-contacts
                        (listEntitiesUpdated)="onTabCountersChange()"
                    ></app-opportunities-single-contacts>
                </ng-template>
            </lib-tab-item>
            <lib-tab-item tabName="Companies" [bodyTemplate]="companiesTab" [headerTemplate]="headerTemplate">
                <ng-template #companiesTab>
                    <app-opportunities-single-companies
                        (listEntitiesUpdated)="onTabCountersChange()"
                    ></app-opportunities-single-companies>
                </ng-template>
            </lib-tab-item>
            <lib-tab-item tabName="Team" [bodyTemplate]="teamTab" [headerTemplate]="headerTemplate">
                <ng-template #teamTab>
                    <app-opportunities-single-team
                        (listEntitiesUpdated)="onTabCountersChange()"
                    ></app-opportunities-single-team>
                </ng-template>
            </lib-tab-item>
            <lib-tab-item tabName="Activity Log" [bodyTemplate]="activityLogTab">
                <ng-template #activityLogTab>
                    <app-opportunities-single-activity-log
                        [opportunityId]="opportunity.id"
                    ></app-opportunities-single-activity-log>
                </ng-template>
            </lib-tab-item>
            <lib-tab-item tabName="Notes" [bodyTemplate]="notesTab" [headerTemplate]="headerTemplate">
                <ng-template #notesTab>
                    <app-notes
                        [entityTypeId]="entityTypeId"
                        [entityId]="opportunity.id"
                        [entityName]="opportunity.name"
                        [organizationId]="opportunity.organizationId"
                        (tabCounterChange)="onTabCountersChange()"
                    ></app-notes>
                </ng-template>
            </lib-tab-item>

            <ng-template #headerTemplate let-tab="tab">
                <lib-counter-badge
                    [label]="tab.tabName()"
                    [count]="tabsCounters[tab.tabName()] || 0"
                ></lib-counter-badge>
            </ng-template>
        </lib-tab-view>
    </div>
</div>
